/**
 * CascadeSignatureBadge — CongressMCP's ambient signal renderer for
 * member entities. Implements the three density modes specced in
 * docs/cascade-signature-spec.md.
 *
 * Wired in via the framework's AmbientSignalRegistry — any
 * EntityRef with entity kind="member" and a hydrated ambient_signal
 * prop dispatches here. Same for any other framework primitive that
 * drops <AmbientSignalSlot /> into its render path.
 *
 * Data contract: see docs/cascade-signature-spec.md §"Data contract".
 * Hydration of the payload is server-side (Step 5: compose_panel
 * batches the matview query for all member bioguides referenced in a
 * spec tree before persisting). This file assumes the data is
 * provided; it does not fetch.
 */
import {
  registerAmbientSignalRenderer,
  type AmbientSignalProps,
} from "../../framework/react/ambient-signal-registry.js";
// React 19 types no longer declare a global JSX namespace.
import type { JSX } from "react";
import { Circle, CircleHelp, Contrast, Diamond, RefreshCw, Triangle, Zap, type LucideIcon } from "lucide-react";

// ── Data contract — mirrors docs/cascade-signature-spec.md ─────────
//
// Kept in this file for now so Step 4 doesn't introduce a new
// types-module sprawl. When a second consumer needs it we'll lift it.

export type CascadePattern =
  | "reactive"
  | "same-week"
  | "two-week"
  | "slow-consistent"
  | "insufficient";

export type CascadeRelationshipType =
  | "coordinator"
  | "cascade_leader"
  | "cascade_follower"
  | "mixed"
  | "key_holdout"
  | "weak";

export interface CascadeSignature {
  bioguide_id: string;
  pattern: CascadePattern;
  relationship_type: CascadeRelationshipType;
  gap_buckets: {
    within_1d: number;
    within_2_3d: number;
    within_4_7d: number;
    within_8_14d: number;
    within_15_30d: number;
  };
  top_followed?: Array<{
    bioguide: string;
    name: string;
    same_day_pct: number;
    lead_rate_pct: number;
  }>;
  top_followers?: Array<{
    bioguide: string;
    name: string;
    cascade_count: number;
    median_gap_days: number;
  }>;
  context?: {
    anchor_kind: "bill" | "member" | "issue";
    anchor_ref: string;
    role_in_context:
      | "sponsor"
      | "original_cosponsor"
      | "early_follower"
      | "late_follower"
      | "key_holdout"
      | null;
    follows_in_context?: {
      bioguide: string;
      name: string;
      gap_days: number | "same_day";
    };
    followed_by_in_context?: Array<{
      bioguide: string;
      name: string;
      gap_days: number | "same_day";
    }>;
  };
  data: {
    matview_refreshed_at: string;
    congress: number;
    sample_size: number;
    sample_threshold: number;
  };
}

// ── Glyph vocabulary ───────────────────────────────────────────────
//
// Lucide icons at row/card density; single letters at chip density (~18px,
// icons shrink toward illegibility). relationship_type takes
// precedence over pattern when picking the glyph.

interface Glyph {
  icon: LucideIcon;
  /** Extra classes for the icon (e.g. rotate the triangle to point down). */
  iconClass?: string;
  letter: string;
  label: string;
}

const PATTERN_GLYPHS: Record<CascadePattern, Glyph> = {
  reactive: { icon: Zap, letter: "R", label: "reactive" },
  "same-week": { icon: RefreshCw, letter: "W", label: "same-week" },
  "two-week": { icon: Contrast, letter: "T", label: "two-week" },
  "slow-consistent": { icon: Circle, letter: "S", label: "slow-consistent" },
  insufficient: { icon: CircleHelp, letter: "?", label: "insufficient" },
};

const COORDINATOR_GLYPH: Glyph = { icon: Diamond, letter: "C", label: "coordinator" };
const KEY_HOLDOUT_GLYPH: Glyph = { icon: Triangle, iconClass: "rotate-180", letter: "H", label: "key holdout" };
const WEAK_GLYPH: Glyph = { icon: CircleHelp, letter: "?", label: "insufficient" };

function GlyphIcon({ glyph, size }: { glyph: Glyph; size: number }) {
  const Icon = glyph.icon;
  return <Icon size={size} strokeWidth={2.25} aria-hidden="true" className={glyph.iconClass} />;
}

function pickGlyph(sig: CascadeSignature): Glyph {
  // Context override: when the entity is a key_holdout in this bill's
  // context, the glyph reflects that — it's the politically loudest signal.
  if (sig.context?.role_in_context === "key_holdout") return KEY_HOLDOUT_GLYPH;
  if (sig.relationship_type === "key_holdout") return KEY_HOLDOUT_GLYPH;
  if (sig.relationship_type === "coordinator") return COORDINATOR_GLYPH;
  if (sig.relationship_type === "weak" || sig.pattern === "insufficient") {
    return WEAK_GLYPH;
  }
  return PATTERN_GLYPHS[sig.pattern];
}

// ── Tone — glyph background palette ─────────────────────────────────

function toneFor(g: Glyph): string {
  switch (g.label) {
    case "coordinator":
      return "bg-stone-900 text-white border-stone-900";
    case "key holdout":
      return "bg-amber-50 text-amber-800 border-amber-300";
    case "reactive":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "same-week":
      return "bg-lime-50 text-lime-800 border-lime-300";
    case "two-week":
      return "bg-stone-100 text-stone-700 border-stone-300";
    case "slow-consistent":
      return "bg-stone-50 text-stone-600 border-stone-200";
    case "insufficient":
    default:
      return "bg-stone-50 text-stone-500 border-stone-200";
  }
}

// ── Micrograph — 5-segment block-fill bar of gap buckets ───────────
//
// Block characters scaled to the bucket's share of the total. Renders
// the cascade-arrival distribution at a glance.

const BLOCK_CHARS = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];

function micrograph(buckets: CascadeSignature["gap_buckets"]): string {
  const vals = [
    buckets.within_1d,
    buckets.within_2_3d,
    buckets.within_4_7d,
    buckets.within_8_14d,
    buckets.within_15_30d,
  ];
  const max = Math.max(...vals, 1);
  return vals
    .map((v) => {
      if (v <= 0) return BLOCK_CHARS[0];
      const idx = Math.min(
        BLOCK_CHARS.length - 1,
        Math.round((v / max) * (BLOCK_CHARS.length - 1)),
      );
      return BLOCK_CHARS[idx];
    })
    .join("");
}

// ── Row-density relationship tag ───────────────────────────────────

function relationshipTagLine(sig: CascadeSignature): string {
  if (sig.context?.role_in_context === "key_holdout") {
    return "key holdout · cohort signed, they didn't";
  }
  const g = pickGlyph(sig);
  // Add the strongest contextual coordinator/follower fact if we have it
  if (sig.top_followed && sig.top_followed.length > 0) {
    const top = sig.top_followed[0];
    if (top.same_day_pct >= 20) {
      return `${g.label} · ${Math.round(top.same_day_pct)}% same-day w/ ${top.name}`;
    }
    if (top.lead_rate_pct >= 20) {
      return `${g.label} · ${Math.round(top.lead_rate_pct)}% led by ${top.name}`;
    }
  }
  return g.label;
}

// ── Freshness footer ───────────────────────────────────────────────

function freshnessLine(sig: CascadeSignature): string {
  const refreshed = new Date(sig.data.matview_refreshed_at);
  const ageMin = Math.max(
    0,
    Math.round((Date.now() - refreshed.getTime()) / 60_000),
  );
  const ageLabel =
    ageMin < 60
      ? `${ageMin}m ago`
      : ageMin < 24 * 60
        ? `${Math.round(ageMin / 60)}h ago`
        : `${Math.round(ageMin / (60 * 24))}d ago`;
  return `signature from ${sig.data.sample_size} cosponsorships · congress ${sig.data.congress} · refreshed ${ageLabel}`;
}

// ── The badge renderer ─────────────────────────────────────────────

export function CascadeSignatureBadge({
  entity_id: _entity_id,
  ambient_signal,
  density,
}: AmbientSignalProps<CascadeSignature>): JSX.Element | null {
  const sig = ambient_signal;
  if (!sig) return null;

  const glyph = pickGlyph(sig);
  const tone = toneFor(glyph);
  const sampleOk = sig.data.sample_size >= sig.data.sample_threshold;

  if (density === "chip") {
    return (
      <span
        className={`inline-flex items-center justify-center ml-1 align-middle text-[10px] font-bold leading-none w-4 h-4 rounded border no-underline ${tone}`}
        title={sampleOk ? glyph.label : "insufficient signal — below threshold"}
        aria-label={`cascade signature: ${glyph.label}`}
      >
        {glyph.letter}
      </span>
    );
  }

  if (density === "row") {
    return (
      <span className="inline-flex items-center gap-2 text-[11px] text-stone-600 align-middle">
        <span
          className={`inline-flex items-center justify-center w-4 h-4 rounded border text-[10px] font-bold leading-none ${tone}`}
          role="img"
          aria-label={`cascade signature: ${glyph.label}`}
        >
          <GlyphIcon glyph={glyph} size={10} />
        </span>
        <span className="text-stone-700">{relationshipTagLine(sig)}</span>
        <span className="font-mono tabular-nums text-stone-500">
          {micrograph(sig.gap_buckets)}
        </span>
      </span>
    );
  }

  // density === "card"
  const ctx = sig.context;
  return (
    <div className="border border-stone-200 rounded-lg bg-white p-2.5 text-[11px] text-stone-700 leading-relaxed">
      <div className="flex items-center gap-2 mb-1">
        <span
          className={`inline-flex items-center justify-center w-5 h-5 rounded border text-[11px] font-bold leading-none ${tone}`}
          role="img"
          aria-label={`cascade signature: ${glyph.label}`}
        >
          <GlyphIcon glyph={glyph} size={12} />
        </span>
        <span className="font-semibold text-stone-900">
          {ctx?.role_in_context === "key_holdout"
            ? `key holdout · ${glyph.label}`
            : glyph.label}
        </span>
      </div>

      {ctx && (
        <div className="text-stone-600 mb-1">
          On{" "}
          <span className="font-mono text-[10px] text-stone-500">
            {ctx.anchor_ref}
          </span>
          {ctx.role_in_context && (
            <>
              :{" "}
              <span className="text-stone-700">
                {ctx.role_in_context.replace(/_/g, " ")}
              </span>
            </>
          )}
          {ctx.follows_in_context && (
            <>
              {" "}
              <span className="text-stone-500">·</span> follows{" "}
              <span className="font-medium">{ctx.follows_in_context.name}</span>{" "}
              <span className="text-stone-500">
                (
                {ctx.follows_in_context.gap_days === "same_day"
                  ? "same day"
                  : `${ctx.follows_in_context.gap_days}d gap`}
                )
              </span>
            </>
          )}
        </div>
      )}

      {sig.top_followed && sig.top_followed.length > 0 && (
        <div className="text-stone-600">
          Follows:{" "}
          {sig.top_followed.slice(0, 3).map((p, i) => (
            <span key={p.bioguide}>
              {i > 0 && <span className="text-stone-500"> · </span>}
              <span className="text-stone-700">{p.name}</span>{" "}
              <span className="text-stone-500 tabular-nums">
                ({Math.round(Math.max(p.same_day_pct, p.lead_rate_pct))}%)
              </span>
            </span>
          ))}
        </div>
      )}

      {sig.top_followers && sig.top_followers.length > 0 && (
        <div className="text-stone-600">
          Pulls:{" "}
          {sig.top_followers.slice(0, 3).map((p, i) => (
            <span key={p.bioguide}>
              {i > 0 && <span className="text-stone-500"> · </span>}
              <span className="text-stone-700">{p.name}</span>{" "}
              <span className="text-stone-500 tabular-nums">
                ({p.median_gap_days}d median)
              </span>
            </span>
          ))}
        </div>
      )}

      <div className="text-stone-500 mt-1 flex items-center gap-2 tabular-nums">
        <span className="font-mono">{micrograph(sig.gap_buckets)}</span>
        <span className="text-[10px]">
          1d / 2-3d / 4-7d / 8-14d / 15-30d
        </span>
      </div>

      <div className="text-[10px] text-stone-500 mt-1 pt-1 border-t border-stone-100">
        {freshnessLine(sig)}
        {!sampleOk && (
          <span className="text-amber-700 ml-1">
            · below threshold ({sig.data.sample_threshold})
          </span>
        )}
      </div>
    </div>
  );
}

// ── Side-effect registration ───────────────────────────────────────
//
// Importing this module from src/app/main.tsx (alongside the framework's
// renderer-registry side-effect import) wires "member" → this component
// into the default AmbientSignalRegistry. From then on, any EntityRef
// or AmbientSignalSlot rendered with entity_type="member" and a hydrated
// cascade_signature payload dispatches here.

registerAmbientSignalRenderer<CascadeSignature>({
  entity_type: "member",
  label: "Cascade signature — temporal cosponsor relationship profile",
  Component: CascadeSignatureBadge,
});
